import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { type SolarReadingMetric } from '../../api-schema-types/solar/SolarMonitoringDomainDtos'
import {
  type MappedForecast,
  type SolarProjectListSummary, type SolarProjectMonitoringForecast,
  type SolarProjectUpdateDto, type SolarYield
} from '../../api-schema-types/solar/SolarProjectDomainDtos'
import CONFIG from '../../app-configs'
import { ApiAuthStrategies } from '../shared/ApiAuthStrategies'

export interface SolarProjectIdProps {
  solarProjectId: number | null
}

export interface SolarForecastIdProps {
  solarProjectId: number
  forecastId: number
}

export interface SolarProjectsFilterProps {
  showInactiveProjects: boolean
}

export const solarApi = createApi({
  reducerPath: 'assetOrderApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${CONFIG.PROXIMA_BACK_END_V2_BASE_API_URL}/solar`,
    prepareHeaders: (headers: Headers, { getState }) => {
      const authToken = ApiAuthStrategies.getBearerTokenHeader()
      headers.set('authorization', authToken)
      return headers
    }
  }),

  tagTypes: ['SolarProjects', 'SolarProject', 'SolarProjectPlantYields'],

  endpoints: (builder) => ({
    getSolarProjects: builder.query({
      query: ({ showInactiveProjects }: SolarProjectsFilterProps) => ({
        url: '/projects'
      }),
      providesTags: ['SolarProjects'],
      transformResponse: (response: SolarProjectListSummary[], meta, arg: SolarProjectsFilterProps) => solarProjectsApiMapper().solarProjectListMapper(response, meta, arg)
    }),

    getSolarProject: builder.query({
      query: ({ solarProjectId }: SolarProjectIdProps) => ({
        url: `/projects/${solarProjectId}`
      }),
      providesTags: ['SolarProject'],
      transformResponse: (response: SolarProjectUpdateDto, meta, arg) => response
    }),

    updateSolarProject: builder.mutation<SolarProjectUpdateDto, Partial<SolarProjectUpdateDto>>({
      query: ({ ...patch }) => ({
        url: `/projects/${patch.identifier?.solarProjectId}`,
        method: 'put',
        body: patch
      }),
      invalidatesTags: ['SolarProjects', 'SolarProject']
    }),

    getSolarForecast: builder.query({
      query: ({ solarProjectId, forecastId }: SolarForecastIdProps) => ({
        url: `/forecasting/forecasts/${solarProjectId}/${forecastId}`
      }),
      providesTags: ['SolarProject'],
      transformResponse: (response: SolarProjectMonitoringForecast, meta, arg) => solarProjectsApiMapper().solarForecastMapper(response, meta, arg)
    }),
    getSolarPlantYield: builder.query({
      query: ({ solarProjectId }: SolarProjectIdProps) => ({
        url: `/forecasting/plant-yields/${solarProjectId}`
      }),
      providesTags: ['SolarProjectPlantYields'],
      transformResponse: (response: SolarYield, meta, arg) => response
    }),

    getMonitoringMetrics: builder.query({
      query: () => ({
        url: '/readings/metrics'
      }),
      transformResponse: (response: SolarReadingMetric[], meta, arg) => response
    })

  })
})

export const useGetSolarProjectsQuery = solarApi.endpoints.getSolarProjects.useQuery
export const useGetSolarProjectQuery = solarApi.endpoints.getSolarProject.useQuery
export const useUpdateSolarProjectMutation = solarApi.endpoints.updateSolarProject.useMutation
export const useGetMonitoringMetricsQuery = solarApi.endpoints?.getMonitoringMetrics.useQuery
export const useGetSolarForecastQuery = solarApi.endpoints.getSolarForecast.useQuery
export const useGetSolarPlantYieldQuery = solarApi.endpoints.getSolarPlantYield.useQuery

// ===================================================================================================================

 
export const solarProjectsApiMapper = () => {
  const solarProjectListMapper = (responseData: SolarProjectListSummary[], meta: any, args: SolarProjectsFilterProps): SolarProjectListSummary[] => {
    if (args.showInactiveProjects) {
      return responseData
    } else {
      return responseData.filter((asp) => asp.summary.generatingOrInBuild)
    }
  }

  const solarForecastMapper = (responseData: SolarProjectMonitoringForecast, meta: any, args: any): MappedForecast => {
    const forecastMap = new Map<number, string[]>()
    for (const forecast of responseData.values) {
      if (!forecastMap.has(forecast.year)) {
        forecastMap.set(forecast.year, new Array<string>(12))
      }

      const forecastYear = forecastMap.get(forecast.year)
      if (forecastYear != null) {
        forecastYear[forecast.month - 1] = forecast.valueKFormatted
      }
    }
    return {
      forecast: responseData,
      forecastMap
    }
  }

  return {
    solarProjectListMapper,
    solarForecastMapper
  }
}
